/**
 * Voice dictation composable.
 *
 * Native app (iOS / Android): on-device streaming recognition through the shared native speech controller
 * (`src/voice/stt/nativeSpeech.ts`). The field shows the words live (`liveText`) with a real input level
 * (`level`); tapping again, or a pause the recognizer ends on, lands the text through `onTranscript`.
 * Each instance has its own owner token, so one composer can never stop another's recording. Going to the
 * background stops and keeps what was said.
 *
 * Web: records a short clip with MediaRecorder, POSTs it to `POST /agent/voice/transcribe` (with the
 * browser language), and delivers the recognized text the same way. Also handles input-device
 * enumeration + selection:
 *   - `availableInputs` is the list of `audioinput` devices.
 *   - `selectedDeviceId` is the user's preferred device, persisted to
 *     localStorage and used for `getUserMedia` calls.
 *   - `pauseForDeviceSwitch()` stops the current recording WITHOUT
 *     transcribing so the user can pick a different mic.
 *   - `resumeRecording()` re-acquires the mic with the currently selected
 *     device and starts a fresh recording.
 *
 * Each call to `useVoiceDictation()` returns its own state so multiple
 * components can mount the same dictation UI without cross-talk.
 */
import { computed, ref } from 'vue'
import { supabase } from '@/supabase'
import { getImpersonationHeaders } from '@/utils/impersonationHeaders'
import { isNativePlatform } from '@/utils/platform'
import { isDesktopApp } from '@/desktop/bridge'
import { desktopPreferredInput } from '@/desktop/preferredMic'
import { useHaptics } from '@/composables/useHaptics'
import { nativeSpeech, SpeechStartError, type SpeechEnd } from '@/voice/stt/nativeSpeech'
import { onAppLifecycle } from '@/voice/platform'
import { isCompanionApp } from '@/native/companion'

export type DictationErrorKind = 'permission' | 'other'

export interface VoiceDictationOptions {
    /** Called once per dictation with its text, even when it repeats the last one. */
    onTranscript?: (text: string) => void
}

const PREFERRED_MIME_TYPES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
]

/** Raw-ish capture: avoid browser AEC/NS/AGC so the recorded blob matches the mic more closely.
 *  `sampleRate` is a hint — many browsers ignore it or use the hardware default. */
const BASE_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
    channelCount: 1,
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
    sampleRate: 16000,
}

const DEVICE_ID_STORAGE_KEY = 'lh.voiceDictation.deviceId'

/** Revoked when a new clip is logged so dev sessions do not leak object URLs. */
let lastDictationDebugObjectUrl: string | null = null

/**
 * Dev-only: log a playable object URL for the exact blob sent to transcribe.
 * Open the URL in a new tab or paste into DevTools console and assign to
 * `new Audio(url).play()`.
 */
function logDictationBlobForDebug(blob: Blob, uploadFilename: string) {
    if (!import.meta.env.DEV) return
    if (lastDictationDebugObjectUrl) {
        URL.revokeObjectURL(lastDictationDebugObjectUrl)
        lastDictationDebugObjectUrl = null
    }
    const url = URL.createObjectURL(blob)
    lastDictationDebugObjectUrl = url
    console.info(
        '[voice-dictation] Debug playback — open this URL in a new tab to hear the clip sent for transcription:',
        url,
        '\nmetadata:',
        { bytes: blob.size, blobType: blob.type, uploadFilename },
    )
}

function pickMimeType(): string {
    if (typeof MediaRecorder === 'undefined') return ''
    for (const mt of PREFERRED_MIME_TYPES) {
        if (MediaRecorder.isTypeSupported(mt)) return mt
    }
    return ''
}

function extensionFor(mime: string): string {
    if (mime.includes('webm')) return 'webm'
    if (mime.includes('ogg')) return 'ogg'
    if (mime.includes('mp4')) return 'm4a'
    return 'webm'
}

function transcribeUrl(): string {
    const base = import.meta.env.VITE_APP_BACKEND_URL || ''
    if (!base) {
        console.warn(
            '[voice] VITE_APP_BACKEND_URL is not set; transcription request will hit current host.'
        )
    }
    return `${base}/agent/voice/transcribe`
}

async function getToken(): Promise<string> {
    const session = (await supabase.auth.getSession())?.data?.session
    return session?.access_token || ''
}

function loadStoredDeviceId(): string | null {
    try {
        return localStorage.getItem(DEVICE_ID_STORAGE_KEY)
    } catch {
        return null
    }
}

function persistDeviceId(id: string | null) {
    try {
        if (id) localStorage.setItem(DEVICE_ID_STORAGE_KEY, id)
        else localStorage.removeItem(DEVICE_ID_STORAGE_KEY)
    } catch {
        // ignore storage errors
    }
}

function dictationLanguage(): string {
    return (typeof navigator !== 'undefined' && navigator.language) || 'en-US'
}

/** FastAPI errors carry `detail` (a string, or `{ message }`); older routes use `error`. */
export function transcribeErrorMessage(status: number, data: unknown): string {
    const d = (data ?? {}) as { error?: unknown; detail?: unknown }
    if (typeof d.error === 'string' && d.error) return d.error
    if (typeof d.detail === 'string' && d.detail) return d.detail
    const nested = (d.detail ?? d.error) as { message?: unknown } | null
    if (nested && typeof nested === 'object' && typeof nested.message === 'string' && nested.message) return nested.message
    if (status === 402) return isCompanionApp() ? 'Voice dictation isn’t available right now.' : 'Voice dictation needs an active plan.'
    if (status === 413) return 'That recording is too long to transcribe.'
    return `Transcription failed (${status}).`
}

export default function useVoiceDictation(options: VoiceDictationOptions = {}) {
    const isRecording = ref(false)
    const isTranscribing = ref(false)
    /** True after the user paused via the input picker; we are not recording
     *  but we are intentionally not running transcription yet either. */
    const isPaused = ref(false)
    const transcript = ref('')
    const voiceError = ref('')
    /** What kind of failure `voiceError` is: a permission denial gets an "Open Settings" action on iOS. */
    const errorKind = ref<DictationErrorKind | null>(null)
    /** Native: the words heard so far in this dictation (shown in the field while recording). */
    const liveText = ref('')
    /** Input level 0..1 while recording (native recognizer level, or the web mic's RMS). */
    const level = ref(0)

    const native = isNativePlatform()
    const speech = native ? nativeSpeech() : null
    const owner = speech?.owner('dictation') ?? null
    const { impact, selectionChanged } = useHaptics()

    const availableInputs = ref<MediaDeviceInfo[]>([])
    const selectedDeviceId = ref<string | null>(loadStoredDeviceId())
    /** The device the live stream is actually using (from track settings).
     *  May differ from `selectedDeviceId` when no preference is set. */
    const activeDeviceId = ref<string | null>(null)

    const currentInputLabel = computed(() => {
        const targetId = activeDeviceId.value || selectedDeviceId.value
        if (targetId) {
            const match = availableInputs.value.find((d) => d.deviceId === targetId)
            if (match?.label) return match.label
        }
        // No active stream yet — fall back to the user's saved preference if any.
        if (selectedDeviceId.value) {
            const match = availableInputs.value.find(
                (d) => d.deviceId === selectedDeviceId.value
            )
            if (match?.label) return match.label
        }
        return 'Default microphone'
    })

    let mediaRecorder: MediaRecorder | null = null
    let mediaStream: MediaStream | null = null
    let chunks: Blob[] = []
    let mimeType = ''
    let abortCtrl: AbortController | null = null
    let deviceChangeHandler: (() => void) | null = null
    let offAppState: (() => void) | null = null

    function setError(message: string, kind: DictationErrorKind = 'other') {
        voiceError.value = message
        errorKind.value = message ? kind : null
    }

    /** Hand finished text to the caller (every time, even when it repeats the last dictation). */
    function deliver(text: string) {
        const t = text.trim()
        if (!t) return
        transcript.value = t
        options.onTranscript?.(t)
    }

    // ── native: the shared recognizer ──────────────────────────────────────────

    function endNativeUi() {
        isRecording.value = false
        liveText.value = ''
        level.value = 0
        offAppState?.()
        offAppState = null
    }

    /** The recognizer stopped by itself (a pause, a final result, an error) or another mic took over. */
    function onNativeEnd(end: SpeechEnd) {
        if (!end.auto) return
        endNativeUi()
        if (end.text) {
            deliver(end.text)
            void selectionChanged()
        } else if (end.error) {
            setError(end.error.message, end.error.kind === 'permission' ? 'permission' : 'other')
        } else if (end.reason === 'silence') {
            setError("I didn't catch anything. Tap the mic and try again.")
        }
    }

    let starting: Promise<void> | null = null

    async function startNative() {
        if (!speech || !owner) return
        liveText.value = ''
        level.value = 0
        // Show "listening" right away; the recognizer takes a beat to spin up.
        isRecording.value = true
        starting = speech.start(owner, {
            onText: (t) => { liveText.value = t },
            onLevel: (l) => { level.value = l },
            onEnd: onNativeEnd,
        }, { language: dictationLanguage() })
        try {
            await starting
            if (!isRecording.value || !speech.isActive(owner)) return
            void impact('light')
            // Leaving the app ends the take: keep what was said rather than recording in the background.
            offAppState = onAppLifecycle({ pause: () => void stopRecording() })
        } catch (e) {
            endNativeUi()
            const err = e instanceof SpeechStartError ? e.error : null
            setError(err?.message || (e as Error)?.message || 'Failed to start voice.', err?.kind === 'permission' ? 'permission' : 'other')
        } finally {
            starting = null
        }
    }

    /**
     * The stop in flight. A second Done tap (or the app pause) while the recognizer winds down shares it and
     * delivers nothing, so the text is inserted once. `cancel()` drops it.
     */
    let stoppingNative: Promise<void> | null = null

    function stopNative(): Promise<void> {
        if (!speech || !owner) return Promise.resolve()
        if (stoppingNative) return stoppingNative
        const sp = speech
        const own = owner
        const take = { run: null as Promise<void> | null }
        const current = () => stoppingNative === take.run
        take.run = (async () => {
            // A second tap while the recognizer is still spinning up: let it start, then stop it cleanly.
            if (starting) await starting.catch(() => {})
            const text = await sp.stop(own)
            if (!current()) return // cancelled meanwhile
            endNativeUi()
            if (text) {
                deliver(text)
                void selectionChanged()
            }
        })().finally(() => {
            if (current()) stoppingNative = null
        })
        stoppingNative = take.run
        return take.run
    }

    // ── web: a level meter on the mic stream (the waveform) ───────────────────

    let meterCtx: AudioContext | null = null
    let meterRaf = 0

    function startMeter(stream: MediaStream) {
        stopMeter()
        try {
            const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
            if (!Ctx || typeof requestAnimationFrame !== 'function') return
            meterCtx = new Ctx()
            void meterCtx.resume?.()
            const src = meterCtx.createMediaStreamSource(stream)
            const analyser = meterCtx.createAnalyser()
            analyser.fftSize = 512
            src.connect(analyser)
            const buf = new Float32Array(analyser.fftSize)
            const loop = () => {
                analyser.getFloatTimeDomainData(buf)
                let sum = 0
                for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
                const db = 20 * Math.log10(Math.sqrt(sum / buf.length) || 1e-8)
                const target = Math.max(0, Math.min(1, (db + 60) / 50))
                level.value += (target - level.value) * (target > level.value ? 0.5 : 0.15)
                meterRaf = requestAnimationFrame(loop)
            }
            meterRaf = requestAnimationFrame(loop)
        } catch {
            stopMeter()
        }
    }

    function stopMeter() {
        if (meterRaf) cancelAnimationFrame(meterRaf)
        meterRaf = 0
        void meterCtx?.close?.().catch(() => {})
        meterCtx = null
        level.value = 0
    }

    function audioConstraints(deviceId: string | null) {
        if (deviceId) {
            return { ...BASE_AUDIO_CONSTRAINTS, deviceId: { exact: deviceId } }
        }
        return { ...BASE_AUDIO_CONSTRAINTS }
    }

    async function refreshInputs(): Promise<MediaDeviceInfo[]> {
        if (native || typeof navigator?.mediaDevices?.enumerateDevices !== 'function') {
            availableInputs.value = []
            return []
        }
        try {
            const all = await navigator.mediaDevices.enumerateDevices()
            availableInputs.value = all.filter((d) => d.kind === 'audioinput')
        } catch {
            availableInputs.value = []
        }
        return availableInputs.value
    }

    function ensureDeviceChangeListener() {
        if (deviceChangeHandler) return
        if (typeof navigator?.mediaDevices?.addEventListener !== 'function') return
        deviceChangeHandler = () => {
            void refreshInputs()
        }
        navigator.mediaDevices.addEventListener('devicechange', deviceChangeHandler)
    }

    function removeDeviceChangeListener() {
        if (
            deviceChangeHandler &&
            typeof navigator?.mediaDevices?.removeEventListener === 'function'
        ) {
            navigator.mediaDevices.removeEventListener(
                'devicechange',
                deviceChangeHandler
            )
        }
        deviceChangeHandler = null
    }

    /**
     * Acquire a mic stream. Call from a click handler so any permission
     * prompt runs in the same user-gesture turn (subsequent calls after
     * permission is granted no longer need a fresh gesture).
     *
     * If `deviceId` is provided, that device is requested explicitly;
     * otherwise the currently selected device (or the system default if
     * none is selected) is used.
     *
     * Native: asks for speech + microphone permission (no stream). A denial
     * throws a `NotAllowedError`.
     */
    async function requestMicrophone(deviceId?: string | null): Promise<MediaStream | null> {
        if (native && speech) {
            const granted = await speech.ensurePermission()
            if (granted !== 'granted') {
                const err = new Error('Microphone access is off.')
                err.name = 'NotAllowedError'
                throw err
            }
            return null
        }
        if (typeof navigator?.mediaDevices?.getUserMedia !== 'function') {
            throw new Error('Microphone not supported in this browser.')
        }
        if (!window.isSecureContext) {
            throw new Error(
                'Microphone requires a secure context (HTTPS or localhost).'
            )
        }
        if (typeof MediaRecorder === 'undefined') {
            throw new Error('Audio recording is not supported in this browser.')
        }

        let targetId =
            deviceId !== undefined ? deviceId : selectedDeviceId.value
        // Desktop app: no mic picked here yet, so use the one chosen in the app's Settings.
        if (targetId == null && deviceId === undefined && isDesktopApp()) {
            targetId = desktopPreferredInput(availableInputs.value.length ? availableInputs.value : await refreshInputs())
        }
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: audioConstraints(targetId ?? null),
        })

        // After permission is granted, labels become populated; refresh once.
        void refreshInputs()
        ensureDeviceChangeListener()
        return stream
    }

    function cleanupStream() {
        stopMeter()
        if (mediaStream) {
            mediaStream.getTracks().forEach((t) => t.stop())
            mediaStream = null
        }
        mediaRecorder = null
        activeDeviceId.value = null
    }

    async function startRecordingWithStream(stream: MediaStream | null) {
        if (isRecording.value || isTranscribing.value) return
        setError('')
        isPaused.value = false

        if (native) {
            await startNative()
            return
        }

        if (!stream) {
            setError('Microphone stream unavailable.')
            return
        }
        mediaStream = stream
        chunks = []
        mimeType = pickMimeType()

        const track = stream.getAudioTracks()[0]
        if (track) {
            const settings = track.getSettings?.()
            activeDeviceId.value = settings?.deviceId ?? null
        }

        try {
            mediaRecorder = mimeType
                ? new MediaRecorder(stream, { mimeType })
                : new MediaRecorder(stream)
            mediaRecorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunks.push(e.data)
            }
            mediaRecorder.onerror = () => {
                setError('Recording failed.')
                cleanupStream()
                isRecording.value = false
            }
            mediaRecorder.start()
            isRecording.value = true
            startMeter(stream)
        } catch (e) {
            const err = e as Error
            setError(err?.message || 'Failed to start recording.')
            cleanupStream()
        }
    }

    async function uploadForTranscription(blob: Blob) {
        isTranscribing.value = true
        const uploadFilename = `dictation.${extensionFor(mimeType)}`
        logDictationBlobForDebug(blob, uploadFilename)
        try {
            const token = await getToken()
            if (!token) {
                setError('Please sign in to use voice dictation.')
                return
            }

            const form = new FormData()
            form.append('file', blob, uploadFilename)
            form.append('language', dictationLanguage())

            abortCtrl = new AbortController()
            const resp = await fetch(transcribeUrl(), {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...getImpersonationHeaders(),
                },
                body: form,
                signal: abortCtrl.signal,
            })

            if (!resp.ok) {
                let data: unknown = null
                try {
                    data = await resp.json()
                } catch {
                    // not JSON
                }
                setError(transcribeErrorMessage(resp.status, data))
                return
            }

            const data = await resp.json()
            deliver(data?.text || '')
        } catch (e) {
            const err = e as Error
            if (err?.name === 'AbortError') return
            setError(err?.message || 'Transcription failed.')
        } finally {
            isTranscribing.value = false
            abortCtrl = null
        }
    }

    /** Stop the recorder without uploading. Returns once tracks are released. */
    async function stopRecorderOnly(): Promise<void> {
        if (native) {
            if (speech && owner) await speech.cancel(owner)
            endNativeUi()
            return
        }
        if (!mediaRecorder) {
            cleanupStream()
            isRecording.value = false
            return
        }
        const recorder = mediaRecorder
        const stopped = new Promise<void>((resolve) => {
            recorder.onstop = () => resolve()
        })
        try {
            recorder.stop()
        } catch {
            // ignore
        }
        isRecording.value = false
        await stopped
        cleanupStream()
    }

    /** Stop and transcribe — the normal "I'm done speaking" flow. */
    async function stopRecording(): Promise<void> {
        if (!isRecording.value) return

        if (native) {
            await stopNative()
            return
        }

        if (!mediaRecorder) return
        await stopRecorderOnly()

        if (chunks.length === 0) return
        const blob = new Blob(chunks, { type: mimeType || 'audio/webm' })
        chunks = []
        if (blob.size === 0) return

        await uploadForTranscription(blob)
    }

    /**
     * Stop recording without producing a transcript. Used when the user
     * opens the input picker to choose a different microphone — we want
     * them to clearly start fresh on the new device. (Web only: native has
     * no device choice.)
     */
    async function pauseForDeviceSwitch(): Promise<void> {
        if (native) return
        if (!isRecording.value) {
            isPaused.value = true
            return
        }
        await stopRecorderOnly()
        chunks = []
        isPaused.value = true
    }

    /**
     * Resume recording after a pause. Re-acquires the mic with the
     * currently selected device.
     */
    async function resumeRecording(): Promise<void> {
        if (!isPaused.value) return
        isPaused.value = false
        try {
            const stream = await requestMicrophone()
            await startRecordingWithStream(stream)
        } catch (e) {
            const err = e as Error
            setError(err?.message || 'Failed to resume recording.')
        }
    }

    /**
     * Select a different input device. Persists the preference. If a
     * pause is in progress, the next `resumeRecording()` will use the
     * new device; if a recording is somehow still live, swap streams.
     */
    async function selectInputDevice(deviceId: string | null): Promise<void> {
        selectedDeviceId.value = deviceId
        persistDeviceId(deviceId)
        if (isRecording.value && !native) {
            // Defensive: if called while still recording, restart with new device.
            await stopRecorderOnly()
            chunks = []
            try {
                const stream = await requestMicrophone(deviceId)
                await startRecordingWithStream(stream)
            } catch (e) {
                const err = e as Error
                setError(err?.message || 'Failed to switch input.')
            }
        }
    }

    /** Abort everything; safe to call from onBeforeUnmount. Only ever touches this instance's own session. */
    function cancel() {
        if (native) {
            stoppingNative = null
            if (speech && owner) void speech.cancel(owner)
            endNativeUi()
        }
        if (mediaRecorder && isRecording.value) {
            try {
                mediaRecorder.stop()
            } catch {
                // ignore
            }
        }
        cleanupStream()
        chunks = []
        isRecording.value = false
        isPaused.value = false
        if (abortCtrl) {
            abortCtrl.abort()
            abortCtrl = null
        }
        isTranscribing.value = false
        removeDeviceChangeListener()
    }

    function clearTranscript() {
        transcript.value = ''
    }

    function clearVoiceError() {
        setError('')
    }

    return {
        // state
        isRecording,
        isTranscribing,
        isPaused,
        transcript,
        voiceError,
        errorKind,
        liveText,
        level,
        availableInputs,
        selectedDeviceId,
        activeDeviceId,
        currentInputLabel,
        // actions
        requestMicrophone,
        startRecordingWithStream,
        stopRecording,
        pauseForDeviceSwitch,
        resumeRecording,
        selectInputDevice,
        refreshInputs,
        cancel,
        clearTranscript,
        clearVoiceError,
        setError,
    }
}
